import { PlaylistLocalStoragePersistence } from "../../localStoragePersistence/v4/playlist/PlaylistLocalStoragePersistence";
import { Client } from "urql";
import {
  PlaylistDTO,
  PlaylistDTOCompanion,
} from "../../dto/v4/playlist/PlaylistDTO";
import PlaylistMapper from "../mapper/PlaylistMapper";
import { PlaylistManipulationUtils } from "../PlaylistManipulationUtils";
import PlaylistData from "../data/PlaylistData";
import { graphql } from "../../gql";
import { TypedDocumentNode } from "@graphql-typed-document-node/core";
import {
  PlaylistQueryQuery,
  PlaylistQueryQueryVariables,
  PlaylistUb,
} from "../../gql/graphql";
import { nullToUndefined } from "../../helper/nullToUndefined";

export interface FetchResult {
  noFetchNecessary: ReadonlyArray<string>;
  successfullyFetched: ReadonlyArray<string>;
  fetchedAndSolvedConflict: ReadonlyArray<{
    duplicatedPlaylistIdentifierOnDisk: string;
    identifierFromServer: string;
  }>;
}

const playlistMapper = new PlaylistMapper();

export class PlaylistFetcher {
  private readonly graphQlClient: Client;
  private readonly mapPersistedToData: (p: PlaylistDTO) => PlaylistData;

  private readonly playlistLocalStoragePersistence =
    new PlaylistLocalStoragePersistence();

  private readonly playlistQuery: TypedDocumentNode<
    PlaylistQueryQuery,
    PlaylistQueryQueryVariables
  > = graphql(`
    query playlistQuery {
      playlists {
        id
        title
        belongsToCrew
        tags
        items {
          songId
          title
          note
        }
        createdAt
        updatedAt
        lastServerVersion
        isRemoved
      }
    }
  `);

  constructor(graphQlClient: Client) {
    this.graphQlClient = graphQlClient;
    this.mapPersistedToData = playlistMapper.persistedToDataMapper();

    this.run = this.run.bind(this);
    this.fetchAllPlaylists = this.fetchAllPlaylists.bind(this);
  }

  run(): Promise<FetchResult> {
    return this.fetchAllPlaylists();
  }

  private fetchAllPlaylists(): Promise<FetchResult> {
    return this.graphQlClient
      .query(this.playlistQuery, {})
      .toPromise()
      .then((result) => {
        if (result.error === undefined && result.data !== undefined) {
          const playlistsFromGraphQl: readonly PlaylistUb[] =
            result.data.playlists;
          const fetchResult = playlistsFromGraphQl
            .map(this.mapFromGraphQlToDtoPlaylist)
            .map((p) => this.updatePlaylist(p))
            .reduce(this.combineFetchResult, emptyFetchResult);
          return Promise.resolve(fetchResult);
        } else {
          return Promise.reject(result);
        }
      });
  }

  private combineFetchResult: (
    acc: FetchResult,
    currentValue: FetchResult,
  ) => FetchResult = (acc: FetchResult, currentValue: FetchResult) => ({
    noFetchNecessary: acc.noFetchNecessary.concat(
      currentValue.noFetchNecessary,
    ),
    successfullyFetched: acc.successfullyFetched.concat(
      currentValue.successfullyFetched,
    ),
    fetchedAndSolvedConflict: acc.fetchedAndSolvedConflict.concat(
      currentValue.fetchedAndSolvedConflict,
    ),
  });

  private mapFromGraphQlToDtoPlaylist: (dto: PlaylistUb) => PlaylistDTO = (
    dto: PlaylistUb,
  ) =>
    PlaylistDTOCompanion.create(
      dto.id,
      dto.title,
      dto.tags,
      nullToUndefined(dto.belongsToCrew),
      dto.items.map((i) => ({
        songId: nullToUndefined(i.songId),
        title: nullToUndefined(i.title),
        note: nullToUndefined(i.note),
      })),
      dto.createdAt,
      dto.updatedAt,
      dto.lastServerVersion,
      false,
      false,
      dto.isRemoved,
    );

  private updatePlaylist(playlistFromServer: PlaylistDTO): FetchResult {
    const optPlaylistOnDisk = this.playlistLocalStoragePersistence
      .load(playlistFromServer.id)
      .orTee((e) =>
        console.error(
          "Could not read playlist. Ignoring it in playlist fetching.",
          e,
        ),
      )
      .unwrapOr(undefined);

    if (optPlaylistOnDisk === undefined) {
      this.playlistLocalStoragePersistence.persist(playlistFromServer);
      return successfullyFetched(playlistFromServer.id);
    } else if (
      optPlaylistOnDisk.lastServerVersion ===
      playlistFromServer.lastServerVersion
    ) {
      return noFetchNecessary(playlistFromServer.id);
    } else if (!optPlaylistOnDisk.needsPush) {
      this.playlistLocalStoragePersistence.persist(playlistFromServer);
      return successfullyFetched(playlistFromServer.id);
    } else {
      return this.solveConflict(playlistFromServer, optPlaylistOnDisk);
    }
  }

  private solveConflict(
    playlistFromServer: PlaylistDTO,
    playlistOnDisk: PlaylistDTO,
  ): FetchResult {
    const playlistOnDiskAsPlaylistData =
      this.mapPersistedToData(playlistOnDisk);
    const duplicatedPlaylist =
      PlaylistManipulationUtils.copyPlaylistFromExistingToCrew(
        playlistOnDiskAsPlaylistData,
        playlistOnDiskAsPlaylistData.belongsToCrew,
      ).copyWithTitle(
        playlistOnDiskAsPlaylistData.title +
          " (Konflikt nach Synchronisation am " +
          new Date().toLocaleString() +
          ")",
      );
    this.playlistLocalStoragePersistence.persist(
      playlistMapper.mapDataToPersisted(duplicatedPlaylist),
    );
    this.playlistLocalStoragePersistence.persist(playlistFromServer);
    return fetchedAndSolvedConflict(
      duplicatedPlaylist.id,
      playlistFromServer.id,
    );
  }
}

const emptyFetchResult: FetchResult = {
  noFetchNecessary: [],
  successfullyFetched: [],
  fetchedAndSolvedConflict: [],
};

function noFetchNecessary(id: string): FetchResult {
  return {
    noFetchNecessary: [id],
    successfullyFetched: [],
    fetchedAndSolvedConflict: [],
  };
}

function successfullyFetched(id: string): FetchResult {
  return {
    noFetchNecessary: [],
    successfullyFetched: [id],
    fetchedAndSolvedConflict: [],
  };
}

function fetchedAndSolvedConflict(
  duplicatedPlaylistIdentifierOnDisk: string,
  identifierFromServer: string,
): FetchResult {
  return {
    noFetchNecessary: [],
    successfullyFetched: [],
    fetchedAndSolvedConflict: [
      {
        duplicatedPlaylistIdentifierOnDisk: duplicatedPlaylistIdentifierOnDisk,
        identifierFromServer: identifierFromServer,
      },
    ],
  };
}
